import type { VorankDrehung } from '../vorank/vorankUnterschriftTypes';

/**
 * Maß-Rechnung für das Einlesen einer unterschriebenen Vorankündigung
 * (Papierweg, docs/vorankuendigung-unterschrift-plan.md §8). Rein, ohne PDF
 * und ohne Canvas — `vorankScanNormalisieren.ts` setzt damit jede gewählte
 * Seite auf ein A4-Hochformat.
 *
 * Drehung immer IM UHRZEIGERSINN, wie „drehen 90°“ im Dialog und wie
 * `/Rotate` im PDF.
 */

export const A4_BREITE_MM = 210;
export const A4_HOEHE_MM = 297;

/** Lange Kante eines A4-Scans bei 300 dpi — mehr Pixel bringen dem Plot nichts. */
export const BILD_LANGE_KANTE_MAX_PX = 3508;

export interface VorankEinpassung {
    /** Faktor Quelle → A4 (mm je Quell-Einheit). */
    skala: number;
    /** Größe des eingepassten (gedrehten) Inhalts auf dem Blatt. */
    breiteMm: number;
    hoeheMm: number;
    /** Linke obere Ecke des Inhalts, gemessen von der linken OBEREN Blattecke (y nach unten). */
    xMm: number;
    yMm: number;
}

const istGedrehtQuer = (drehung: VorankDrehung): boolean => drehung === 90 || drehung === 270;

/**
 * Die gedrehte Quelle so groß wie möglich auf A4 hoch (210 × 297 mm), mittig,
 * nie verzerrt und ohne zusätzlichen Rand: eine A4-Seite bleibt 1:1, ein
 * US-Letter wird auf 210 mm Breite gebracht, ein Handyfoto füllt die Seite in
 * einer Richtung ganz. Nur das Seitenverhältnis der Quelle zählt — die
 * Einheit ist gleich, solange beide Kanten dieselbe haben.
 */
export function einpassungA4(quelle: { breiteMm: number; hoeheMm: number }, drehung: VorankDrehung): VorankEinpassung {
    const { breiteMm: b0, hoeheMm: h0 } = quelle;
    if (!(Number.isFinite(b0) && Number.isFinite(h0) && b0 > 0 && h0 > 0)) {
        throw new Error('Die Seitengröße der eingelesenen Datei ist ungültig.');
    }
    const b = istGedrehtQuer(drehung) ? h0 : b0;
    const h = istGedrehtQuer(drehung) ? b0 : h0;
    const skala = Math.min(A4_BREITE_MM / b, A4_HOEHE_MM / h);
    const breiteMm = b * skala;
    const hoeheMm = h * skala;
    return {
        skala,
        breiteMm,
        hoeheMm,
        xMm: (A4_BREITE_MM - breiteMm) / 2,
        yMm: (A4_HOEHE_MM - hoeheMm) / 2,
    };
}

/**
 * Vorschlag für den Dialog: eine Querseite war fast immer ein hochkant
 * gemeintes Blatt, das quer gescannt oder fotografiert wurde → 90°. Ob es
 * 90° oder 270° sein muss, sieht nur der Mensch — der Dialog lässt drehen.
 */
export function drehungVorschlag(breiteMm: number, hoeheMm: number): VorankDrehung {
    return breiteMm > hoeheMm ? 90 : 0;
}

/**
 * Welche Seiten der Dialog vorab mitnimmt: die ersten `fassungSeiten` (die
 * Vorankündigung), alles danach ist in aller Regel das mitgedruckte
 * Begleitblatt. Hat die Datei weniger Seiten — oder ist die Seitenzahl der
 * Fassung unbekannt —, sind alle an.
 */
export function vorauswahlSeiten(seitenGesamt: number, fassungSeiten: number): boolean[] {
    const gesamt = Number.isFinite(seitenGesamt) ? Math.max(0, Math.floor(seitenGesamt)) : 0;
    const grenze = Number.isFinite(fassungSeiten) && fassungSeiten >= 1 ? Math.floor(fassungSeiten) : gesamt;
    return Array.from({ length: gesamt }, (_, i) => i < grenze);
}

/**
 * Zielgröße eines Bildes (Foto, gerasterte PDF-Seite): die lange Kante höchstens
 * `langeKanteMax` Pixel, Seitenverhältnis bleibt, nie vergrößert.
 */
export function bildZielPixel(
    breitePx: number,
    hoehePx: number,
    langeKanteMax: number = BILD_LANGE_KANTE_MAX_PX,
): { breitePx: number; hoehePx: number } {
    if (!(Number.isFinite(breitePx) && Number.isFinite(hoehePx) && breitePx > 0 && hoehePx > 0)) {
        throw new Error('Die Bildgröße der eingelesenen Datei ist ungültig.');
    }
    const lang = Math.max(breitePx, hoehePx);
    const faktor = lang > langeKanteMax ? langeKanteMax / lang : 1;
    return {
        breitePx: Math.max(1, Math.round(breitePx * faktor)),
        hoehePx: Math.max(1, Math.round(hoehePx * faktor)),
    };
}

/** Matrix [a b c d e f] eines `cm`-Operators. */
export type VorankMatrix = [number, number, number, number, number, number];

/**
 * Die Matrix, die einen Inhalt der Größe `inhaltB × inhaltH` (eigene
 * Einheiten: pt bei einer eingebetteten PDF-Seite, 1 × 1 bei einem Bild)
 * mit `skalaX`/`skalaY` vergrößert, im Uhrzeigersinn um `drehung` dreht und
 * mit der linken UNTEREN Ecke auf (x0|y0) legt (PDF-Koordinaten, pt).
 *
 * Eine einzige Matrix statt translate/rotate/scale (wie `vorankPlotBlatt`):
 * so steht die Drehung exakt im Content-Stream, ohne cos(90°) ≈ 6e-17.
 */
export function einbettungsMatrix(a: {
    drehung: VorankDrehung;
    inhaltB: number;
    inhaltH: number;
    skalaX: number;
    skalaY: number;
    x0: number;
    y0: number;
}): VorankMatrix {
    const { drehung, inhaltB: w, inhaltH: h, skalaX: sx, skalaY: sy, x0, y0 } = a;
    switch (drehung) {
        // Quellpunkt (u|v) → (x0 + sy·v | y0 + sx·(w − u)): die linke untere Ecke
        // wandert nach links oben, die Oberkante an die rechte Seite.
        case 90: return [0, -sx, sy, 0, x0, y0 + sx * w];
        case 180: return [-sx, 0, 0, -sy, x0 + sx * w, y0 + sy * h];
        // (u|v) → (x0 + sy·(h − v) | y0 + sx·u)
        case 270: return [0, sx, -sy, 0, x0 + sy * h, y0];
        default: return [sx, 0, 0, sy, x0, y0];
    }
}

/** Seitendrehung eines PDF (`/Rotate`, beliebige Vielfache von 90, auch negativ) → 0/90/180/270. */
export function normiereDrehung(grad: number): VorankDrehung {
    if (!Number.isFinite(grad)) return 0;
    const n = ((Math.round(grad / 90) * 90) % 360 + 360) % 360;
    return n as VorankDrehung;
}

/** Zwei Drehungen hintereinander (Seitendrehung im PDF + Drehung im Dialog). */
export function addiereDrehung(a: VorankDrehung, b: VorankDrehung): VorankDrehung {
    return normiereDrehung(a + b);
}
